import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Building2, Mail, Phone, Globe } from "lucide-react";
import { hqApi, formatApiError } from "@/hq/hqApi";
import { Card, Skeleton, StageBadge, PriorityBadge } from "@/hq/HQPrimitives";
import ActivityTimeline from "@/hq/ActivityTimeline";

const STAGES = ["new","contacted","qualified","discovery","quoted","won","lost"];
const PRIORITIES = ["low","normal","high","urgent"];

function LabelValue({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">{label}</div>
      <div className="mt-1 text-[15px] text-[#0A0A0A] break-words">{children ?? "—"}</div>
    </div>
  );
}

export default function HQLeadDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = () => {
    setData(null);
    hqApi.get(`/leads/${id}`).then((r) => setData(r.data)).catch(() => setErr("Something went wrong loading this record."));
  };
  useEffect(reload, [id]);

  async function updateField(field, value) {
    setSaving(true);
    try {
      const { data: resp } = await hqApi.patch(`/leads/${id}`, { [field]: value });
      setData((d) => ({ ...d, lead: resp.lead }));
      toast.success(`Lead ${field} updated`);
      // reload activities to include the new lead_updated event
      const { data: fresh } = await hqApi.get(`/leads/${id}`);
      setData(fresh);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  }

  if (err) return <Card className="p-6" data-testid="hq-lead-error">{err}</Card>;
  if (!data) return (
    <div className="space-y-4">
      <Skeleton h={40} w={220} />
      <Card className="p-6 space-y-3"><Skeleton h={20}/><Skeleton h={20}/><Skeleton h={20}/></Card>
    </div>
  );

  const { lead, business, contact, website, service, enquiry, activities } = data;
  const fd = enquiry?.form_data || {};

  return (
    <div className="space-y-5" data-testid="hq-lead-detail">
      <Link to="/hq/leads" className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.24em] uppercase text-[#8A8A8A] hover:text-[#0A0A0A]">
        <ArrowLeft size={14} /> All leads
      </Link>

      <Card className="p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Lead</div>
            <h1 className="mt-1 font-black text-2xl md:text-3xl tracking-[-0.02em]">
              {business?.name || "Unknown business"}
            </h1>
            <div className="mt-2 text-[13px] text-[#4A4A4A]">
              {service?.name || "—"} · {lead.source || "—"} · {new Date(lead.created_at).toLocaleString()}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <StageBadge value={lead.stage} />
            <PriorityBadge value={lead.priority} />
            {/* Phase 3I.4L — jump to the linked Client profile. Reuses the
                canonical /hq/businesses/{business_id} route used everywhere
                else in HQ (see HQBusinesses.jsx, HQContacts.jsx, the
                Executive Dashboard). Falls back to a disabled chip with a
                tooltip when a historical lead has no linked business — no
                automatic client creation. */}
            {business?.id ? (
              <Link
                to={`/hq/businesses/${business.id}`}
                data-testid="hq-lead-detail-open-client"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.16em] uppercase bg-[#FF6A00] text-black hover:bg-[#e75f00] transition-colors"
              >
                <Building2 size={11}/> Open Client
              </Link>
            ) : (
              <span
                data-testid="hq-lead-detail-open-client-disabled"
                title="No linked client"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.16em] uppercase bg-white border border-[#EAEAEA] text-[#C9C9C9] cursor-not-allowed"
              >
                <Building2 size={11}/> Open Client
              </span>
            )}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">Stage</div>
            <select
              value={lead.stage || "new"}
              onChange={(e) => updateField("stage", e.target.value)}
              disabled={saving}
              data-testid="hq-lead-stage-select"
              className="mt-1 w-full px-3 py-2.5 rounded-full border border-[#EAEAEA] bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-[#FF6A00]"
            >
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">Priority</div>
            <select
              value={lead.priority || "normal"}
              onChange={(e) => updateField("priority", e.target.value)}
              disabled={saving}
              data-testid="hq-lead-priority-select"
              className="mt-1 w-full px-3 py-2.5 rounded-full border border-[#EAEAEA] bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-[#FF6A00]"
            >
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="p-6 lg:col-span-2 space-y-6">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">CRM Discovery</div>
            <h2 className="mt-1 font-black text-lg tracking-[-0.01em]">Submitted enquiry</h2>
          </div>
          {!enquiry ? (
            <div className="text-sm text-[#8A8A8A]">No enquiry linked.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <LabelValue label="Name" testid="hq-lead-fd-name">{fd.name}</LabelValue>
              <LabelValue label="Business name">{fd.business_name}</LabelValue>
              <LabelValue label="Email">{fd.email}</LabelValue>
              <LabelValue label="Phone">{fd.phone}</LabelValue>
              <LabelValue label="Industry">{fd.industry}</LabelValue>
              <LabelValue label="Company size">{fd.company_size}</LabelValue>
              <LabelValue label="Current website">{fd.current_url}</LabelValue>
              <LabelValue label="Currency">{fd.currency}</LabelValue>
              <LabelValue label="Language">{fd.language}</LabelValue>
              <LabelValue label="Interested in" testid="hq-lead-interested-in">
                {fd.interested_in
                  ? String(fd.interested_in).replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase())
                  : "—"}
              </LabelValue>
              <LabelValue label="Source">{fd.source || enquiry.source}</LabelValue>
              <div className="sm:col-span-2">
                <LabelValue label="Current system / description">{fd.description}</LabelValue>
              </div>
              <div className="sm:col-span-2">
                <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">Needs</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Array.isArray(fd.needs) && fd.needs.length ? fd.needs.map((n, i) => (
                    <span key={i} className="px-3 py-1 rounded-full bg-[#0A0A0A] text-white text-[11px] font-bold tracking-[0.08em]">{n}</span>
                  )) : <span className="text-[#8A8A8A] text-sm">—</span>}
                </div>
              </div>
              <div className="sm:col-span-2">
                <LabelValue label="Biggest problem">{fd.problem}</LabelValue>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-6 space-y-4">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Relationships</div>
            <h2 className="mt-1 font-black text-lg tracking-[-0.01em]">Related records</h2>
          </div>
          {business && (
            <Link to={`/hq/businesses/${business.id}`}
                  className="flex items-center gap-3 p-3 rounded-xl border border-[#EAEAEA] hover:border-[#FF6A00] transition-colors"
                  data-testid="hq-lead-related-business">
              <div className="w-9 h-9 rounded-full bg-[#FFF7EE] grid place-items-center text-[#FF6A00]">
                <Building2 size={16} />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#8A8A8A]">Business</div>
                <div className="font-bold text-[14px] truncate">{business.name}</div>
              </div>
            </Link>
          )}
          {contact && (
            <div className="p-3 rounded-xl border border-[#EAEAEA]">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#8A8A8A]">Primary contact</div>
              <div className="font-bold text-[14px]">{contact.full_name || contact.email}</div>
              <div className="mt-1 text-[13px] text-[#4A4A4A] space-y-1">
                {contact.email && <div className="flex items-center gap-2"><Mail size={12} />{contact.email}</div>}
                {contact.phone && <div className="flex items-center gap-2"><Phone size={12} />{contact.phone}</div>}
              </div>
            </div>
          )}
          {website && (
            <div className="p-3 rounded-xl border border-[#EAEAEA]">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#8A8A8A]">Website</div>
              <div className="font-bold text-[14px] break-all">{website.normalized_domain}</div>
              {website.url && (
                <a href={website.url} target="_blank" rel="noreferrer"
                   className="mt-1 inline-flex items-center gap-1 text-[12px] text-[#FF6A00] hover:text-[#D94C00] break-all">
                  <Globe size={12} /> {website.url}
                </a>
              )}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-6">
        <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Timeline</div>
        <h2 className="mt-1 font-black text-lg tracking-[-0.01em]">Activity</h2>
        <div className="mt-6"><ActivityTimeline items={activities || []} /></div>
      </Card>
    </div>
  );
}
